import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { useDarkMode } from "../../../../../context/ThemeContext";
import { brand } from "../../../../../config/theme";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useIsReseller } from "../../../../../hooks/useIsReseller";
import { getThemeClasses, getCustomSelectStyles, DENSE_LIST } from "../../../shared";
import {
  useAvisosStore,
  type Aviso,
  type AvisoFormPayload,
} from "../../../../../store/admin/avisosStore";
import { useAvisosList } from "../../../../../features/avisos/queries";
import {
  useCompaniesStore,
  type Company,
} from "../../../../../store/admin/companiesStore";

import AvisosHeader from "./AvisosHeader";
import AvisoCard from "./AvisoCard";
import AvisoListRow from "./AvisoListRow";
import AddAvisoModal from "./modals/AddAvisoModal";
import EditAvisoModal from "./modals/EditAvisoModal";
import DetailsAvisoModal from "./modals/DetailsAvisoModal";
import type { AvisoFormData } from "./types";
import PaginationControls from "../../../../../components/base/PaginationControls";

const emptyFormValues: AvisoFormData = {
  title: "",
  description: "",
  status: "active",
};

/**
 * Mirror `buildCompanyOptions` from CompanySelector so the names shown across
 * the app stay consistent (header selector + avisos cards + scope banners).
 */
const labelForCompany = (company?: Company | null): string =>
  company?.name || company?.business_name || company?.id || "";

export default function AvisosSection() {
  const { t } = useTranslation("adminSections");
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);
  const customSelectStyles = getCustomSelectStyles(isDarkMode);

  const { isReseller } = useIsReseller();

  // Avisos store (mutaciones)
  const createAviso = useAvisosStore((s) => s.createAviso);
  const updateAviso = useAvisosStore((s) => s.updateAviso);
  const deleteAviso = useAvisosStore((s) => s.deleteAviso);

  const ITEMS_PER_PAGE = 12;

  // Companies — name resolution comes from the same store that powers the
  // header CompanySelector, so labels stay in sync.
  const allCompanies = useCompaniesStore((s) => s.allCompanies);
  const fetchAllCompanies = useCompaniesStore((s) => s.fetchAllCompanies);
  const selectedCompanyId = useCompaniesStore((s) => s.companyId);

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [sendToAllMode, setSendToAllMode] = useState(false);
  const [editingAviso, setEditingAviso] = useState<Aviso | null>(null);
  const [viewingAviso, setViewingAviso] = useState<Aviso | null>(null);

  // Image
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [removeImage, setRemoveImage] = useState(false);

  // Loading
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  // Filters
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedScope, setSelectedScope] = useState("all");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // View mode
  const [selectedView, setSelectedView] = useState<"grid" | "list">("list");

  // Tracks avisos with an in-flight status toggle to prevent double-clicks
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

  // Form
  const form = useForm<AvisoFormData>({
    mode: "onChange",
    defaultValues: emptyFormValues,
  });

  const companiesById = useMemo(() => {
    const map = new Map<string, Company>();
    allCompanies.forEach((c) => map.set(c.id, c));
    return map;
  }, [allCompanies]);

  // Currently selected company in the header — same source AdminLayout uses.
  const selectedCompany = selectedCompanyId
    ? companiesById.get(selectedCompanyId) || null
    : null;
  const selectedCompanyName = labelForCompany(selectedCompany);

  const filterParams = useMemo(
    () => ({
      page: currentPage,
      limit: ITEMS_PER_PAGE,
      status: selectedStatus !== "all" ? selectedStatus : undefined,
      scope:
        selectedScope === "global" || selectedScope === "company"
          ? selectedScope
          : undefined,
      search: debouncedSearch || undefined,
    }),
    [currentPage, selectedStatus, selectedScope, debouncedSearch],
  );

  const avisosQuery = useAvisosList(filterParams);
  const avisos = avisosQuery.data?.data ?? [];
  const loading = avisosQuery.isLoading;
  const meta = avisosQuery.data?.meta;
  const totalPages = meta?.totalPages || 1;

  const queryClient = useQueryClient();
  const invalidateAvisos = () => {
    queryClient.invalidateQueries({ queryKey: ["avisos"] });
    queryClient.invalidateQueries({ queryKey: ["resellerCounts"] });
    queryClient.invalidateQueries({ queryKey: ["counts"] });
  };

  // Companies para resolver nombres (mismo origen que el selector del header)
  useEffect(() => {
    fetchAllCompanies();
  }, [fetchAllCompanies]);

  // Reset a la primera página cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedStatus, selectedScope, debouncedSearch]);

  const resetFormState = () => {
    form.reset(emptyFormValues);
    setImagePreview(null);
    setImageFile(null);
    setRemoveImage(false);
    setEditingAviso(null);
  };

  // Add
  const handleAddClick = () => {
    resetFormState();
    setSendToAllMode(false);
    setShowAddModal(true);
  };

  const handleSendToAllClick = () => {
    resetFormState();
    setSendToAllMode(true);
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setSendToAllMode(false);
    resetFormState();
  };

  // Edit
  const handleEditClick = (aviso: Aviso) => {
    setEditingAviso(aviso);
    setImagePreview(aviso.image || null);
    setImageFile(null);
    setRemoveImage(false);
    form.reset({
      title: aviso.title || "",
      description: aviso.description || "",
      status: aviso.status,
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    resetFormState();
  };

  // View details
  const handleViewDetailsClick = (aviso: Aviso) => {
    setViewingAviso(aviso);
  };

  const handleCloseDetailsModal = () => {
    setViewingAviso(null);
  };

  const handleEditFromDetails = (aviso: Aviso) => {
    setViewingAviso(null);
    handleEditClick(aviso);
  };

  // Image handlers
  const handleImageChange = (file: File | null) => {
    if (!file) return;
    setImageFile(file);
    setRemoveImage(false);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleClearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(!!editingAviso?.image);
  };

  /** Resolve the company_id payload from the current UI state. */
  const resolveCompanyIdForCreate = (): string | null => {
    if (sendToAllMode) return null;
    return selectedCompanyId || null;
  };

  const resolveCompanyIdForEdit = (): string | undefined => {
    // Don't touch company_id on edit — scope is decided at creation.
    return undefined;
  };

  // Scope text for the modal banner
  const getScopeForAdd = (): {
    kind: "global" | "company";
    label: string;
  } => {
    if (sendToAllMode || !selectedCompanyId) {
      return {
        kind: "global",
        label: t("avisos.scopeGlobalLabel"),
      };
    }
    const name = selectedCompanyName || t("avisos.selectedCompany");
    return {
      kind: "company",
      label: t("avisos.scopeCompanyLabel", { name }),
    };
  };

  const getScopeForEdit = (): {
    kind: "global" | "company";
    label: string;
  } => {
    if (!editingAviso?.company_id) {
      return {
        kind: "global",
        label: t("avisos.scopeGlobalLabel"),
      };
    }
    const name =
      labelForCompany(companiesById.get(editingAviso.company_id)) ||
      t("avisos.specificCompany");
    return {
      kind: "company",
      label: t("avisos.scopeCompanyLabel", { name }),
    };
  };

  // Submit
  const submitForm = async (data: AvisoFormData) => {
    const title = (data.title || "").trim();
    const hasImage = !!imageFile || (!!imagePreview && !removeImage);

    if (!title && !hasImage) {
      await Swal.fire({
        icon: "warning",
        title: t("avisos.alerts.requiredDataTitle"),
        text: t("avisos.alerts.requiredDataText"),
        confirmButtonColor: brand.primary,
      });
      return;
    }

    setLoadingSubmit(true);

    const basePayload: AvisoFormPayload = {
      title,
      description: (data.description || "").trim(),
      status: data.status,
      image: imageFile,
      removeImage,
    };

    let result;
    if (editingAviso) {
      const editCompanyId = resolveCompanyIdForEdit();
      const payload: AvisoFormPayload =
        editCompanyId === undefined
          ? basePayload
          : { ...basePayload, company_id: editCompanyId };
      result = await updateAviso(editingAviso.id, payload);
    } else {
      const payload: AvisoFormPayload = {
        ...basePayload,
        company_id: resolveCompanyIdForCreate(),
      };
      result = await createAviso(payload);
    }

    setLoadingSubmit(false);

    if (result.success) {
      await Swal.fire({
        icon: "success",
        title: editingAviso
          ? t("avisos.alerts.updatedTitle")
          : t("avisos.alerts.createdTitle"),
        confirmButtonColor: brand.primary,
      });
      if (editingAviso) handleCloseEditModal();
      else handleCloseAddModal();
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        invalidateAvisos();
      }
    } else {
      await Swal.fire({
        icon: "error",
        title: t("avisos.alerts.errorTitle"),
        text: result.error || t("avisos.alerts.saveError"),
        confirmButtonColor: "#ef4444",
      });
    }
  };

  // Card actions
  const handleToggleStatus = async (aviso: Aviso) => {
    if (togglingIds.has(aviso.id)) return;
    setTogglingIds((prev) => {
      const next = new Set(prev);
      next.add(aviso.id);
      return next;
    });
    try {
      const newStatus = aviso.status === "active" ? "inactive" : "active";
      const result = await updateAviso(aviso.id, { status: newStatus });
      if (!result.success) {
        await Swal.fire({
          icon: "error",
          title: t("avisos.alerts.errorTitle"),
          text: result.error || t("avisos.alerts.statusError"),
          confirmButtonColor: "#ef4444",
        });
      } else {
        invalidateAvisos();
      }
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(aviso.id);
        return next;
      });
    }
  };

  const handleDelete = async (aviso: Aviso) => {
    const confirmation = await Swal.fire({
      title: t("avisos.alerts.deleteTitle"),
      text: t("avisos.alerts.deleteConfirmText", {
        title: aviso.title ? ` "${aviso.title}"` : "",
      }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: t("avisos.alerts.deleteConfirmButton"),
      cancelButtonText: t("avisos.alerts.cancelButton"),
    });
    if (!confirmation.isConfirmed) return;

    const result = await deleteAviso(aviso.id);
    if (result.success) {
      invalidateAvisos();
      await Swal.fire({
        icon: "success",
        title: t("avisos.alerts.deletedTitle"),
        confirmButtonColor: brand.primary,
      });
    } else {
      await Swal.fire({
        icon: "error",
        title: t("avisos.alerts.errorTitle"),
        text: result.error || t("avisos.alerts.deleteError"),
        confirmButtonColor: "#ef4444",
      });
    }
  };

  const addScope = getScopeForAdd();
  const editScope = editingAviso ? getScopeForEdit() : null;

  return (
    <div className="h-full flex flex-col min-w-0 overflow-x-hidden">
      <AvisosHeader
        isDarkMode={isDarkMode}
        themeClasses={themeClasses}
        customSelectStyles={customSelectStyles}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        selectedScope={selectedScope}
        setSelectedScope={setSelectedScope}
        search={search}
        setSearch={setSearch}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
        onAddClick={handleAddClick}
        onSendToAllClick={handleSendToAllClick}
        isReseller={isReseller}
      />

      <div className="flex-1 overflow-y-auto p-3 sm:p-6">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        ) : avisos.length === 0 ? (
          <div
            className={`flex flex-col items-center justify-center w-full py-16 ${themeClasses.textSecondary}`}
          >
            <i className="ri-megaphone-line text-5xl mb-3"></i>
            <p>{t("avisos.noAvisos")}</p>
          </div>
        ) : (
          <>
            {selectedView === "grid" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {avisos.map((aviso) => (
                  <AvisoCard
                    key={aviso.id}
                    aviso={aviso}
                    isDarkMode={isDarkMode}
                    themeClasses={themeClasses}
                    companyName={
                      aviso.company_id
                        ? labelForCompany(companiesById.get(aviso.company_id))
                        : undefined
                    }
                    isToggling={togglingIds.has(aviso.id)}
                    onViewDetails={handleViewDetailsClick}
                    onEdit={handleEditClick}
                    onToggleStatus={handleToggleStatus}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            ) : (
              <div className={DENSE_LIST}>
                {avisos.map((aviso) => (
                  <AvisoListRow
                    key={aviso.id}
                    aviso={aviso}
                    isDarkMode={isDarkMode}
                    themeClasses={themeClasses}
                    companyName={
                      aviso.company_id
                        ? labelForCompany(companiesById.get(aviso.company_id))
                        : undefined
                    }
                    isToggling={togglingIds.has(aviso.id)}
                    onViewDetails={handleViewDetailsClick}
                    onEdit={handleEditClick}
                    onToggleStatus={handleToggleStatus}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className="mt-6">
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {showAddModal && (
        <AddAvisoModal
          isDarkMode={isDarkMode}
          themeClasses={themeClasses}
          form={form}
          loadingSubmit={loadingSubmit}
          scopeLabel={addScope.label}
          scopeKind={addScope.kind}
          imagePreview={imagePreview}
          onImageChange={handleImageChange}
          onClearImage={handleClearImage}
          onClose={handleCloseAddModal}
          onSubmit={submitForm}
        />
      )}

      {showEditModal && editingAviso && editScope && (
        <EditAvisoModal
          isDarkMode={isDarkMode}
          themeClasses={themeClasses}
          form={form}
          loadingSubmit={loadingSubmit}
          scopeLabel={editScope.label}
          scopeKind={editScope.kind}
          imagePreview={imagePreview}
          onImageChange={handleImageChange}
          onClearImage={handleClearImage}
          onClose={handleCloseEditModal}
          onSubmit={submitForm}
        />
      )}

      {viewingAviso && (
        <DetailsAvisoModal
          aviso={viewingAviso}
          isDarkMode={isDarkMode}
          themeClasses={themeClasses}
          companyName={
            viewingAviso.company_id
              ? labelForCompany(companiesById.get(viewingAviso.company_id))
              : undefined
          }
          onClose={handleCloseDetailsModal}
          onEdit={handleEditFromDetails}
        />
      )}
    </div>
  );
}
